import axios from 'axios';
import { useCallback, useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';

// Redux.
import { connect } from 'react-redux';
import {
    setSearchText,
    setTrack,
} from '../redux/actions/dataActions';

import ProfilePicture from './ProfilePicture';
import SearchTranscript from './SearchTranscript'
import Sections from './Sections';
import ShareTrackDrawer from './ShareTrackDrawer';
import SyncTranscriptAudio from './SyncTranscriptAudio';
import SpeedActions from './SpeedActions';
import Transcript from './Transcript'
import BookmarkButton from './BookmarkButton';

import { cancelPendingAudioSeek, loadTrack } from "./audioHelper"

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime'

import { getAnalytics, logEvent } from "firebase/analytics";

// MUI components.
import Drawer from '@mui/material/Drawer';

import { buildMissingRoute } from '../utils/missingRoute';
import { isVideoRoute, useVideoNavigation } from '../utils/videoNavigation';
import { getYouTubeVideoId } from './youtubeHelper';
import VideoAudioContinuation from './VideoAudioContinuation';
import { audioContinuation, consumeContinuation, finishedProgress, flushPlayback, playbackRecord as readPlaybackRecord, recordPlayback, resumeProgress, useEpisodePlayback } from '../utils/episodePlayback';
import { registerAudioPlaybackTrack } from '../utils/audioProgressRecorder';

import {
    Close,
    FastForwardRounded,
    FastRewindRounded,
    IosShareRounded,
    MoreHorizRounded,
    PlayArrowRounded,
} from '@mui/icons-material';

// TODO remove package from npm?
// import { useSwipeable } from 'react-swipeable';

import { eventLog } from './eventHelper';
import { largeSourceImage } from './trackHelper';
import { episodePlaybackLabel } from '../utils/episodePlaybackLabel';

import CloseFooter from './CloseFooter';
import EntityInfoDrawer from './EntityInfoDrawer';
import NoteMessage from './NoteMessage';
import useNoteDrawerPlayback from './useNoteDrawerPlayback';
import PlayPauseBar from './PlayPauseBar';
import './EpisodePlaybackGlass.css';
import TrackDetails, { TrackTranscriptUnavailableRow } from './TrackDetails';
import {
    displaySpeakerName,
    canOpenSpeakerProfile,
} from './speakerHelper';

const EpisodeActionRow = ({
    Icon,
    title,
    onClick,
}) => (
    <button type='button' className='previewInfoOptionsRow' onClick={onClick}>
        <span className='previewInfoOptionsRowMain'>
            <Icon className='previewInfoOptionsRowIcon' />
            <span>{title}</span>
        </span>
    </button>
);

// TODO rename track player?
const AudioPlayer = ({
    close,
    preservePlaybackOnClose = false,
    onEpisodeLoaded,
    source,
    trackId,

    // Redux.
    authenticated,
    uid,
    forMeSessionRevision,
    setSearchText,
    setTrack,
    track,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { openVideo } = useVideoNavigation();
    const progressOwner = useRef(authenticated ? uid : null);
    progressOwner.current = authenticated ? uid : null;
    // A queued flush must stay retired even if the same account signs in again.
    const progressSession = useRef(null);
    if (!progressSession.current || progressSession.current.authenticated !== authenticated ||
        progressSession.current.uid !== uid || progressSession.current.revision !== forMeSessionRevision) {
        progressSession.current = { authenticated, uid, revision: forMeSessionRevision };
    }
    const capturedProgressSession = progressSession.current;
    const progressMounted = useRef(true);
    useEffect(() => {
        progressMounted.current = true;
        return () => { progressMounted.current = false; };
    }, []);
    const ownsProgressSession = () => progressMounted.current && progressSession.current === capturedProgressSession;
    const videoOpened = useRef(false);
    const [videoUnavailable, setVideoUnavailable] = useState(false);
    const missingRoute = buildMissingRoute(location);
    const audio = document.getElementById('audio');
    const [playbackTiming, setPlaybackTiming] = useState(() => ({
        source: audio?.src,
        durationSeconds: audio?.duration,
        playbackRate: audio?.playbackRate || 1,
    }));
    useEffect(() => {
        if (!audio) return undefined;
        const updateTiming = () => setPlaybackTiming({
            source: audio.src,
            durationSeconds: audio.duration,
            playbackRate: audio.playbackRate || 1,
        });
        const events = ['loadedmetadata', 'durationchange', 'ratechange', 'play', 'emptied'];
        events.forEach(event => audio.addEventListener(event, updateTiming));
        return () => events.forEach(event => audio.removeEventListener(event, updateTiming));
    }, [audio]);
    const sectionsContainerRef = useRef(null);
    const transcriptContainerRef = useRef(null);
    const bottomControlsRef = useRef(null);
    const [bottomControlsHeight, setBottomControlsHeight] = useState(0);

    // TODO have one curtime for audio and save local cur time.
    const [curTime, setCurTime] = useState(0);
    const [currentSpeaker, setCurrentSpeaker] = useState(null);
    const canOpenCurrentSpeaker = canOpenSpeakerProfile(currentSpeaker);
    const CurrentSpeakerHeader = canOpenCurrentSpeaker ? 'button' : 'div';
    const [localCurTime, setLocalCurTime] = useState(0);
    const [listenSeconds, setListenSeconds] = useState([]);
    const [localTrack, setLocalTrack] = useState(null);
    const [openPersonInfo, setOpenPersonInfo] = useState(false);
    const [openSearchTranscript, setOpenSearchTranscript] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [playbackStarted, setPlaybackStarted] = useState(false);
    const [sharedStartSeconds, setSharedStartSeconds] = useState(null);
    const [showTimeDrawer, setShowTimeDrawer] = useState(false);
    const [pendingTimeAdjustment, setPendingTimeAdjustment] = useState(null);
    const timeAdjustmentRequest = useRef(null);
    const [scrollToCurrentMessage, setScrollToCurrentMessage] = useState(true);
    const [selectedSpeakerId, setSelectedSpeakerId] = useState(null);
    const [resetPathnameOnClose, setResetPathnameOnClose] = useState(null);
    const [trackPreviews, setTrackPreviews] = useState(null);
    const resumeAfterPersonInfo = useRef(false);
    const resumeAfterShare = useRef(false);

    const [showTranscript, setShowTranscript] = useState(false);
    const owner = authenticated ? uid : null;
    const currentTrack = useRef();
    const audioSource = localTrack?.mp3URL ? new URL(localTrack.mp3URL, document.baseURI).href : null;
    const ownsAudio = useCallback(() => Boolean(audioSource && audio?.src === audioSource), [audio, audioSource]);
    currentTrack.current = { owner, trackId, source: audioSource };
    useEffect(() => {
        if (localTrack) onEpisodeLoaded?.(localTrack);
    }, [localTrack, onEpisodeLoaded]);
    const playbackRecord = useEpisodePlayback(owner, localTrack);
    const videoId = getYouTubeVideoId(localTrack?.youtube);
    const continuation = sharedStartSeconds !== null ? null : audioContinuation(playbackRecord, videoId, localTrack?.totalTimeSeconds);
    const dismissContinuation = () => {
        if (continuation !== null) consumeContinuation(owner, localTrack, videoId);
    };
    const saveAudioPosition = seconds => {
        if (!ownsProgressSession() || progressOwner.current !== owner || !localTrack) return;
        recordPlayback(owner, localTrack, 'audio', { seconds,
            durationSeconds: localTrack.totalTimeSeconds, resetsCompletion: true });
        flushPlayback(owner, ownsProgressSession);
    };
    const cancelTimeAdjustment = () => {
        timeAdjustmentRequest.current = null;
        setPendingTimeAdjustment(null);
    };
    const changeTimeDrawer = open => {
        cancelTimeAdjustment();
        setShowTimeDrawer(open);
    };
    const prepareAudioPlayback = () => {
        cancelTimeAdjustment();
        dismissContinuation();
        const savedAudio = readPlaybackRecord(owner, localTrack).audioProgress;
        if (finishedProgress(savedAudio) && sharedStartSeconds === null) {
            saveAudioPosition(0);
            if (audio.src === localTrack.mp3URL) audio.currentTime = 0;
            setCurTime(0);
            setLocalCurTime(0);
        }
    };
    const continueFromVideo = () => {
        if (continuation === null || progressOwner.current !== owner) return;
        setShowTimeDrawer(false);
        const request = { owner, trackId: localTrack.id, pathname: localTrack.pathname,
            source: audioSource, seconds: continuation };
        timeAdjustmentRequest.current = request;
        setPendingTimeAdjustment(request);
        consumeContinuation(owner, localTrack, videoId, true);
        saveAudioPosition(continuation);
        setTrack(localTrack);
        cancelPendingAudioSeek(audio);
        if (audio.src !== localTrack.mp3URL) loadTrack(localTrack.mp3URL, continuation, false);
        else { audio.currentTime = continuation; audio.play().catch(() => {}); }
        setPlaybackStarted(true);
        setSharedStartSeconds(null);
    };
    useEffect(() => {
        timeAdjustmentRequest.current = null;
        setPendingTimeAdjustment(null);
        setShowTimeDrawer(false);
    }, [owner, trackId]);
    useEffect(() => {
        if (!pendingTimeAdjustment || !audio) return undefined;
        const request = pendingTimeAdjustment;
        let active = true;
        const clear = () => {
            active = false;
            if (timeAdjustmentRequest.current === request) {
                timeAdjustmentRequest.current = null;
                setPendingTimeAdjustment(null);
            }
        };
        const showAdjustments = event => {
            if (!active || timeAdjustmentRequest.current !== request) return;
            const current = currentTrack.current;
            if (current.owner !== request.owner || current.trackId !== request.trackId ||
                current.source !== request.source || audio.src !== request.source || audio.error ||
                window.location.pathname !== request.pathname) {
                clear();
                return;
            }
            if (audio.seeking || audio.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
            if (Math.abs(audio.currentTime - request.seconds) > 1) {
                // A newer seek must not open controls for the superseded handoff.
                if (event?.type === 'seeked') clear();
                return;
            }
            clear();
            setShowTimeDrawer(true);
        };
        const events = ['seeked', 'loadeddata', 'canplay'];
        const cancelPausedHandoff = () => { if (audio.paused) clear(); };
        events.forEach(event => audio.addEventListener(event, showAdjustments));
        audio.addEventListener('error', clear);
        audio.addEventListener('pause', cancelPausedHandoff);
        showAdjustments();
        return () => {
            active = false;
            events.forEach(event => audio.removeEventListener(event, showAdjustments));
            audio.removeEventListener('error', clear);
            audio.removeEventListener('pause', cancelPausedHandoff);
        };
    }, [audio, pendingTimeAdjustment]);
    useEffect(() => {
        if (!localTrack || videoOpened.current || !(location.state?.resumeVideo || location.state?.videoToPlay)) return;
        videoOpened.current = true;
        const matched = getYouTubeVideoId(localTrack.youtube);
        const savedVideoId = location.state.videoToPlay;
        if (matched && (!savedVideoId || matched === savedVideoId)) openVideo(localTrack.id, matched, localTrack);
        else setVideoUnavailable(true);
    }, [localTrack, location.state, openVideo]);
    useEffect(() => {
        registerAudioPlaybackTrack(owner, localTrack);
    }, [localTrack, owner]);

    useEffect(() => {
        const element = bottomControlsRef.current;
        if (!element) return undefined;
        const measure = () => setBottomControlsHeight(element.getBoundingClientRect().height);
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(element);
        return () => observer?.disconnect();
    }, [localTrack, playbackStarted, continuation]);

    const analytics = getAnalytics();
    const [currentSection, setCurrentSection] = useState(0);

    const fetchTrack = () => {
        axios
            .get(`/track/${trackId}`)
            .then((res) => {
                registerAudioPlaybackTrack(owner, res.data);
                setLocalTrack(res.data);
                setTrack(res.data)
                // dispatch({
                //     type: SET_TRACK,
                //     payload: res.data
                // });
            })
            .catch((err) => {
                console.log(err);
                navigate(missingRoute)
                // dispatch({
                //   type: SET_ERRORS,
                //   payload: err.response.data
                // });
            });
    }

    useEffect(() => {
        if (!localTrack || !localTrack.sectionInfos) {
            return;
        }
        for (let i = localTrack.sectionInfos.length - 1; i >= 0; i--) {
            const sectionStartTime = localTrack.sectionInfos[i].startTime;
            if (localCurTime >= sectionStartTime) {
                setCurrentSection(Math.min(Math.max(i, 0), localTrack.sectionInfos.length - 1));
                return;
            }
        }

        // Default to 0.
        setCurrentSection(0);
    }, [localCurTime])

    useEffect(() => {
        if (showTranscript) {
            setScrollToCurrentMessage(true);
        } else {
            // Keep the drawer's transitioning ancestors stationary.
            transcriptContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
        }
    }, [showTranscript])

    const togglePersonInfo = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        if (!open) {
            if (resumeAfterPersonInfo.current && ownsAudio()) {
                audio.play().catch(function () { });
            }
            resumeAfterPersonInfo.current = false;
        } else {
            resumeAfterPersonInfo.current = ownsAudio() && !audio.paused && !audio.ended;
            if (ownsAudio()) audio.pause();
        }
        setOpenPersonInfo(open);
    }

    const addListenTime = (seconds, forceWrite) => {
        if (!localTrack || !audio || localTrack.mp3URL !== audio.src || openPersonInfo) {
            return;
        }
        const floorSeconds = Math.floor(seconds);

        let newListenSeconds = listenSeconds;
        if (!listenSeconds.includes(floorSeconds)) {
            newListenSeconds = listenSeconds.concat([floorSeconds]);
            setListenSeconds(newListenSeconds);
        }

        if ((forceWrite || newListenSeconds.length > 10) && newListenSeconds.length > 0) {
            newListenSeconds.sort(function (a, b) { return a - b });
            var ranges = {};
            var currentStart = newListenSeconds[0];
            var currentLength = 0;
            for (var i = 1; i < newListenSeconds.length; i++) {
                if (newListenSeconds[i] === currentStart + currentLength + 1) {
                    currentLength += 1;
                } else {
                    if (currentLength > 0) {
                        ranges[currentStart] = currentLength;
                    }
                    currentStart = newListenSeconds[i];
                    currentLength = 0;
                }
            }
            if (currentLength > 0) {
                ranges[currentStart] = currentLength;
            }
            for (const [start, duration] of Object.entries(ranges)) {
                const end = parseInt(start) + parseInt(duration);
                logEvent(analytics, 'listen_time', {
                    trackId: localTrack.id,
                    startSecs: start,
                    endSecs: end,
                    durationSecs: duration,
                    playbackRate: audio.playbackRate,
                    playbackSecs: duration / audio.playbackRate,
                });
            }
            setListenSeconds([]);
        }
    }

    useEffect(() => {
        if (!localTrack && track && track.id === trackId && track.pathname.startsWith('/track/')) {
            setLocalTrack(track);
        }
    }, [track])

    useEffect(() => {
        if (audio && localTrack && audio.src === localTrack.mp3URL) {
            setLocalCurTime(curTime);
        }
    }, [curTime])

    useEffect(() => {
        dayjs.extend(relativeTime);

        if (!localTrack) {
            if (track && track.id === trackId && track.pathname.startsWith('/track/')) {
                setLocalTrack(track);
            } else {
                fetchTrack()
            }
        }

        if (trackPreviews === null) {
            axios
                .get(`/track_previews/${trackId}`)
                .then((res) => {
                    if (res.data && res.data.previews) {
                        setTrackPreviews(res.data.previews);
                    } else {
                        setTrackPreviews([]);
                    }
                });
        }

        const setAudioTime = () => {
            if (audio.src !== currentTrack.current.source) return;
            setCurTime(audio.currentTime);
            setPlaying(!audio.paused && !audio.ended);
        }

        const onPause = (event) => {
            setPlaying(false);
        }

        const onPlay = (event) => {
            setPlaying(audio.src === currentTrack.current.source);
        }

        // DOM listeners: update React state on DOM events
        audio.addEventListener('timeupdate', setAudioTime);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('play', onPlay);

        // effect cleanup
        return () => {
            audio.removeEventListener('timeupdate', setAudioTime);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('play', onPlay);
        }
    }, [])

    useEffect(() => {
        if (!localTrack) return undefined;
        const onPlaybackStart = () => {
            const matching = audio.src === audioSource;
            setPlaybackStarted(matching);
            if (matching) {
                setSharedStartSeconds(null);
            }
        };
        audio.addEventListener('play', onPlaybackStart);
        return () => audio.removeEventListener('play', onPlaybackStart);
    }, [audio, localTrack, audioSource]);

    useEffect(() => {
        if (localTrack) {
            // An existing episode keeps its controls even while paused. Preparing
            // new media alone should leave the initial play-and-duration action.
            setPlaybackStarted(audio.src === localTrack.mp3URL);
            setSharedStartSeconds(null);
            // A refreshed video route can restore these details behind its player.
            // The foreground video still owns the URL and media session.
            if (isVideoRoute(window.location.pathname)) return;
            if (window.location.pathname === localTrack.pathname) {
                // Extra check to make sure the track is correct.
                // The delay for React to update the state causes the track to mismatch in some
                // cases (i.e. when hitting "continue conversation"), which causes loadTrack to
                // called with the wrong mp3 file.
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);
                const startParam = urlParams.get('start');
                const requestedStart = Number(startParam?.replace('s', ''));
                const hasRequestedStart = Boolean(startParam?.trim()) &&
                    Number.isFinite(requestedStart) && requestedStart >= 0;
                const savedAudio = readPlaybackRecord(progressOwner.current, localTrack).audioProgress;
                const start = hasRequestedStart ? requestedStart : savedAudio ? resumeProgress(savedAudio) : localTrack.lastListenSeconds || 0;
                const sectionsParam = urlParams.get('sections');
                const shouldOpenSections = sectionsParam === '1' || sectionsParam === 'true';
                if (hasRequestedStart && urlParams.get('autoplay') !== 'true' &&
                    (urlParams.get('source') !== 'note' || urlParams.get('autoplay') === 'false')) {
                    // Shared links select a moment, but playback still waits for
                    // Play. Keep unrelated media and its pending seek untouched.
                    if (ownsAudio()) {
                        cancelPendingAudioSeek(audio);
                        audio.pause();
                        audio.currentTime = start;
                    }
                    setCurTime(start);
                    setLocalCurTime(start);
                    setSharedStartSeconds(start);
                    setPlaybackStarted(false);
                    setPlaying(false);
                } else if (urlParams.get('autoplay') === 'false' ||
                    (!hasRequestedStart && audio.src && !ownsAudio() && urlParams.get('autoplay') !== 'true')) {
                    // Browsing another episode owns its selected position, not the
                    // shared audio element. Explicit Play transfers that ownership.
                    if (!ownsAudio()) {
                        setCurTime(start);
                        setLocalCurTime(start);
                    }
                    setPlaying(ownsAudio() && !audio.paused && !audio.ended);
                } else if (hasRequestedStart) {
                    if (audio.src === localTrack.mp3URL) {
                        cancelPendingAudioSeek(audio);
                        audio.currentTime = start;
                        audio.play().catch(() => {});
                    } else {
                        loadTrack(localTrack.mp3URL, start, false);
                    }
                } else {
                    loadTrack(localTrack.mp3URL, start, true);
                }
                if (audio.src === localTrack.mp3URL) {
                    setCurTime(audio.currentTime);
                    setLocalCurTime(audio.currentTime);
                }
                if (shouldOpenSections && localTrack.aiFeatures && localTrack.sectionInfos?.length > 0) {
                    setShowSectionOverview(true);
                }
            } else {
                setResetPathnameOnClose(window.location.pathname);
                window.history.replaceState(window.history.state, '', `/track/${localTrack.id}`);
            }
        }
    }, [localTrack, audio, ownsAudio])

    const [shareTrackDrawerOpen, setShareTrackDrawerOpen] = useState(false);
    const toggleShareTrackDrawerOpen = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        if (open) {
            resumeAfterShare.current = ownsAudio() && !audio.paused && !audio.ended;
            if (ownsAudio()) audio.pause();
        } else {
            if (resumeAfterShare.current && ownsAudio()) {
                audio.play().catch(function () { });
            }
            resumeAfterShare.current = false;
        }
        setShareTrackDrawerOpen(open);
    }

    useEffect(() => {
        addListenTime(curTime, false);
    }, [curTime])

    useEffect(() => {
        if (!playing) {
            addListenTime(audio.currentTime, true);
        }
    }, [playing])

    const seek = (timeSeconds) => {
        cancelTimeAdjustment();
        dismissContinuation();
        playSharedMoment(timeSeconds);
    }

    const exit = () => {
        if (showTranscript) {
            setShowTranscript(false);
        } else {
            if (!preservePlaybackOnClose && ownsAudio()) audio.pause();
            if (close) {
                if (resetPathnameOnClose && resetPathnameOnClose !== null) {
                    window.history.replaceState(window.history.state, '', resetPathnameOnClose);
                }
                close();
            } else {
                window.location = "/"
            }
        }
    }

    const toggleSearchTranscript = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setOpenSearchTranscript(open);
        if (!open) {
            setSearchText('');
        }
    }

    function sectionPrev() {
        for (let i = track.sectionInfos.length - 1; i >= 0; i--) {
            const sectionTime = track.sectionInfos[i].startTime;
            if (sectionTime < curTime - 4) {
                seek(sectionTime + 0.01);
                setScrollToCurrentMessage(true);
                return;
            }
        }
        seek(localTrack.transcript[0].timeSeconds);
        setScrollToCurrentMessage(true);
    }

    function sectionNext() {
        const section = localTrack.sectionInfos[currentSection];
        eventLog(10, {
            'trackId': localTrack.id,
            'sectionStartIndex': section.startIndex,
            'topicIds': section.topics,
        })

        for (let i = 0; i < localTrack.sectionInfos.length; i++) {
            const sectionTime = localTrack.sectionInfos[i].startTime;
            if (sectionTime > curTime + 1) {
                seek(sectionTime + 0.01);
                setScrollToCurrentMessage(true);
                return;
            }
        }
        const end = ownsAudio() && Number.isFinite(audio.duration) ? audio.duration : localTrack.totalTimeSeconds;
        seek(Math.max(0, end - 0.1));
        setScrollToCurrentMessage(true);
    };

    const openSpeakerInfo = (speakerId) => () => {
        if (speakerId !== "unknown") {
            setSelectedSpeakerId(speakerId);
            togglePersonInfo(true)();
        }
    }

    const [showSectionOverview, setShowSectionOverview] = useState(false);

    const toggleSectionOverview = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setShowSectionOverview(open);
    };

    const [showSpeedControls, setShowSpeedControls] = useState(false);
    const toggleSpeedControls = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setShowSpeedControls(open);
    };

    const [showEpisodeActions, setShowEpisodeActions] = useState(false);
    const toggleEpisodeActions = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setShowEpisodeActions(open);
    };

    const onShareEpisodeActionClick = (event) => {
        setShowEpisodeActions(false);
        toggleShareTrackDrawerOpen(true)(event);
    };

    const onPodcastClick = () => {
        if (localTrack && localTrack.source) {
            navigate(`/podcast/${localTrack.source.id}`);
        }
    }

    const onSearchClick = () => {
        setOpenSearchTranscript(true);
    }

    const onSectionsClick = () => {
        setShowSectionOverview(true);
    }

    const onTranscriptClick = () => {
        setShowTranscript(true);
    }

    const onSyncClick = () => {
        if (!audio || audio.src !== localTrack?.mp3URL) return;
        setScrollToCurrentMessage(true);
        // Resume directly in the tap handler to retain browser user activation.
        if (audio.paused && !audio.ended) audio.play().catch(() => {});
    }

    const [showNoteDrawer, setShowNoteDrawer] = useState(false);
    useNoteDrawerPlayback(showNoteDrawer && ownsAudio());
    const onAddNoteClick = () => {
        setShowNoteDrawer(true);
    }

    const hasSharedStart = sharedStartSeconds !== null;
    const initialPlayLabel = episodePlaybackLabel(
        localTrack?.totalTimeSeconds,
        sharedStartSeconds ?? localCurTime,
        false,
        hasSharedStart,
    );
    const playSharedMoment = (seconds) => {
        setTrack(localTrack);
        saveAudioPosition(seconds);
        cancelPendingAudioSeek(audio);
        // Keep the requested time if playback is blocked, so a retry starts at
        // the selected moment rather than a stale media position.
        setSharedStartSeconds(seconds);
        if (audio.src !== localTrack.mp3URL) {
            loadTrack(localTrack.mp3URL, seconds, true);
        } else {
            audio.currentTime = seconds;
            audio.play().catch(() => {});
        }
        setCurTime(seconds);
        setLocalCurTime(seconds);
    };
    const startPlayback = () => {
        if (hasSharedStart) {
            playSharedMoment(sharedStartSeconds);
            return;
        }
        setTrack(localTrack);
        if (audio.src !== localTrack.mp3URL) {
            const savedAudio = readPlaybackRecord(owner, localTrack).audioProgress;
            loadTrack(localTrack.mp3URL, savedAudio ? resumeProgress(savedAudio) : localCurTime, true);
        } else {
            audio.play().catch(() => {});
        }
    };

    return (
        <div className={`hideScrollbar trackAudioPlayer${showTranscript ? ' trackAudioPlayerTranscript' : ''}${!playbackStarted ? ' trackAudioPlayerInitial' : ''}${hasSharedStart ? ' trackAudioPlayerSharedStart' : ''}`} style={bottomControlsHeight > 0 ? { '--track-playback-bottom': `${bottomControlsHeight}px` } : undefined}>
            <EntityInfoDrawer
                open={openPersonInfo}
                onClose={togglePersonInfo(false)}
                speakerId={selectedSpeakerId} />

            <Drawer
                open={showNoteDrawer}
                onClose={() => setShowNoteDrawer(false)}
                // PaperProps={{
                //     sx: {
                //         borderRadius: '20px 20px 0px 0px'
                //     }
                // }}
                anchor='bottom'>
                <NoteMessage
                    open={showNoteDrawer}
                    trackId={trackId}
                    exit={() => { setShowNoteDrawer(false) }} />
            </Drawer>

            <div ref={transcriptContainerRef} className="container hideScrollbar appDrawerSurface" style={{ height: 'var(--app-height, 100svh)' }} >
                <div className='horizontalCenter'>
                    <div>
                        <div className='horizontalCenter' style={{ background: 'transparent' }}>
                            <div style={{ background: 'var(--app-background)', minHeight: 'var(--app-height, 100svh)' }}>
                                {openPersonInfo || openSearchTranscript || !localTrack ?
                                    <div className='glow'>
                                        <div style={{ height: 'var(--app-height, 100svh)', padding: '16px', overflowY: 'auto', paddingTop: 'var(--track-header-height)' }}>
                                            {!!source ?
                                                <div style={{ textAlign: 'center', paddingTop: '20px' }}>
                                                    <div>
                                                        <img src={largeSourceImage(source.imageURL)} className='largeSourceImage podcastArtwork' />
                                                    </div>
                                                    <div style={{ margin: '20px 0px 40px', fontSize: '16px', fontWeight: '500' }}>
                                                        {source.title}
                                                    </div>

                                                    <div className="loadingSpinner"></div>
                                                </div>
                                                :
                                                <div>
                                                    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
                                                        <div style={{ padding: '20px', fontSize: '14px', fontWeight: '500', color: 'var(--app-text-secondary)' }}>
                                                            Loading Episode...
                                                        </div>
                                                        <div className="loadingSpinner"></div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    :
                                    !showTranscript ?
                                        <div style={{ paddingTop: 'var(--track-header-height)', paddingBottom: 'calc(var(--track-playback-bottom) + 16px)' }}>
                                            <TrackDetails
                                                track={localTrack}
                                                userId={authenticated ? uid : null}
                                                curTime={localCurTime}
                                                durationSeconds={ownsAudio() && playbackTiming.source === audio.src &&
                                                    Number.isFinite(playbackTiming.durationSeconds) && playbackTiming.durationSeconds > 0
                                                    ? playbackTiming.durationSeconds : localTrack.totalTimeSeconds}
                                                playbackRate={playbackTiming.playbackRate}
                                                showsPlaybackProgress={ownsAudio()}
                                                onTimestampPlay={seek}
                                                previews={trackPreviews}
                                                onAddNoteClick={onAddNoteClick}
                                                onPodcastClick={onPodcastClick}
                                                onSearchClick={onSearchClick}
                                                onSectionsClick={onSectionsClick}
                                                onTranscriptClick={onTranscriptClick} />
                                        </div> :
                                        !localTrack.aiFeatures ?
                                            <div className='trackTranscriptUnavailablePane'>
                                                <TrackTranscriptUnavailableRow />
                                            </div>
                                            :
                                            <Transcript
                                                autoscrollPaddingBottom={230}
                                                currentSection={currentSection}
                                                curTime={curTime}
                                                isCurrentViewport={true}
                                                openSpeakerInfo={openSpeakerInfo}
                                                playing={playing}
                                                scrollToCurrentMessage={scrollToCurrentMessage}
                                                setScrollToCurrentMessage={setScrollToCurrentMessage}
                                                setCurrentSpeaker={setCurrentSpeaker}
                                                transcriptContainerRef={transcriptContainerRef}
                                                track={localTrack}
                                            />
                                }
                            </div>
                        </div>

                        {!localTrack || !localTrack.aiFeatures || !showTranscript ? <></> :
                            <SyncTranscriptAudio
                                bottom='calc(var(--track-playback-bottom) + 8px)'
                                scrollToCurrentMessage={scrollToCurrentMessage}
                                onSyncClick={onSyncClick}
                                track={localTrack}
                                onSearchClick={onSearchClick}
                                onSectionsClick={onSectionsClick}
                            />
                        }
                    </div>

                    <Drawer
                        // PaperProps={{
                        //     sx: {
                        //         borderRadius: '10px 10px 0px 0px'
                        //     }
                        // }}
                        anchor='bottom' open={openSearchTranscript} onClose={toggleSearchTranscript(false)}>
                        <div className='horizontalCenter appDrawerSurface' style={{ height: 'var(--app-height, 100svh)' }}>
                            <SearchTranscript onMessageTimeClick={seek} curTime={curTime} close={toggleSearchTranscript(false)} />
                        </div>
                    </Drawer>

                    <Drawer
                        PaperProps={{
                            sx: {
                                borderRadius: '18px 18px 0px 0px'
                            }
                        }}
                        anchor='bottom' open={showSpeedControls} onClose={toggleSpeedControls(false)}>
                        <div className='horizontalCenter appDrawerSurface hasCloseFooter'>
                            <SpeedActions />
                            <CloseFooter onClick={toggleSpeedControls(false)} />
                        </div>
                    </Drawer>

                    <Drawer
                        PaperProps={{
                            sx: {
                                borderRadius: '18px 18px 0px 0px'
                            }
                        }}
                        anchor='bottom' open={showEpisodeActions} onClose={toggleEpisodeActions(false)}>
                        <div className='horizontalCenter appDrawerSurface hasCloseFooter'>
                            <div style={{ padding: '0px 0px 0px' }}>
                                <div className='previewInfoOptionsDrawer'>
                                    <div className='previewInfoOptionsRows'>
                                        <BookmarkButton
                                            trackId={localTrack?.fullTrackId ?? localTrack?.id}
                                            source={localTrack?.source ?? {}}
                                            renderMode='row'
                                        />
                                        <EpisodeActionRow
                                            Icon={IosShareRounded}
                                            title='Share episode'
                                            onClick={onShareEpisodeActionClick}
                                        />
                                    </div>
                                </div>
                                <CloseFooter onClick={toggleEpisodeActions(false)} />
                            </div>
                        </div>
                    </Drawer>

                    <ShareTrackDrawer
                        open={shareTrackDrawerOpen}
                        curTime={localCurTime}
                        currentSection={currentSection}
                        close={toggleShareTrackDrawerOpen(false)}
                        track={localTrack}
                    />

                    <Drawer
                        // PaperProps={{
                        //     sx: {
                        //         borderRadius: '20px 20px 0px 0px'
                        //     }
                        // }}
                        anchor='bottom' open={showSectionOverview} onClose={toggleSectionOverview(false)}>
                        <div className='sectionsDrawerViewport'>
                            <div className='horizontalCenter appDrawerSurface'>
                                <Sections playing={playing} track={localTrack} currentSection={currentSection}
                                    close={toggleSectionOverview(false)} sectionsContainerRef={sectionsContainerRef}
                                    curTime={localCurTime} onSeek={seek} onTogglePlayback={() => {
                                        if (ownsAudio() && !audio.paused && !audio.ended) audio.pause();
                                        else seek(localCurTime);
                                    }} />
                            </div>
                        </div>
                    </Drawer>

                    <div className='header'>
                        <div className='horizontalCenter'>
                            <div className={showTranscript ? undefined : 'blurBackgroundNoShadow'}>
                                <div className='trackPlayerToolbar'>
                                    <button
                                        type='button'
                                        onClick={exit}
                                        aria-label={showTranscript ? 'Close transcript' : 'Close episode'}
                                        className={showTranscript ? 'trackPlayerIconButton episodePlaybackGlass' : 'trackPlayerIconButton'}
                                    >
                                        <Close style={{ verticalAlign: 'middle', fontSize: '24px', color: 'var(--app-text-secondary)' }} />
                                    </button>
                                    <div className='trackPlayerToolbarActions'>
                                        {showTranscript && !!currentSpeaker &&
                                            <CurrentSpeakerHeader
                                                type={canOpenCurrentSpeaker ? 'button' : undefined}
                                                onClick={canOpenCurrentSpeaker ? openSpeakerInfo(currentSpeaker.id) : undefined}
                                                className='trackTranscriptSpeakerButton episodePlaybackGlass'
                                            >
                                                <ProfilePicture
                                                    speakerId={currentSpeaker.id}
                                                    borderSize={1}
                                                    imageSrc={currentSpeaker.imageURL}
                                                    anonymousDisplay={currentSpeaker.anonymousDisplay}
                                                    size='24px'
                                                />
                                                <span className='trackTranscriptSpeakerName'>
                                                    {displaySpeakerName(currentSpeaker)}
                                                </span>
                                            </CurrentSpeakerHeader>
                                        }
                                        {localTrack &&
                                            <button
                                                type='button'
                                                aria-label='Episode actions'
                                                onClick={toggleEpisodeActions(true)}
                                                className={showTranscript ? 'trackPlayerIconButton episodePlaybackGlass' : 'trackPlayerIconButton clampSafeButton'}
                                            >
                                                <MoreHorizRounded style={{ verticalAlign: 'middle', fontSize: '24px', color: 'var(--app-text-secondary)' }} />
                                            </button>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {localTrack && (
                        <div>
                            {playbackStarted && showTranscript && scrollToCurrentMessage && localTrack.aiFeatures && (
                                <div className='sectionsControlContainer'>
                                    <div className='trackSectionBar episodePlaybackGlass'>
                                        <button type='button' aria-label='Previous section' onClick={sectionPrev}>
                                            <FastRewindRounded aria-hidden='true' />
                                        </button>
                                        <button type='button' onClick={toggleSectionOverview(true)}>
                                            <span className='sectionsLabelText'>
                                                {localTrack.sectionInfos[currentSection].title}
                                            </span>
                                        </button>
                                        <button type='button' aria-label='Next section' onClick={sectionNext}>
                                            <FastForwardRounded aria-hidden='true' />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div ref={bottomControlsRef} className='trackBottomControls'>
                                {videoUnavailable && <p className='trackVideoUnavailable' role='status'>Saved video unavailable. You can still play audio.</p>}
                                {continuation !== null && <VideoAudioContinuation duration={localTrack.totalTimeSeconds}
                                    seconds={continuation} expanded={playbackStarted} playbackRate={audio.playbackRate || 1}
                                    onContinue={continueFromVideo} onDismiss={dismissContinuation} />}
                                {playbackStarted ? (
                                    <PlayPauseBar onMoreClick={toggleSpeedControls(true)} totalSeconds={localTrack.totalTimeSeconds} showTranscript={showTranscript} hideSecondaryControls={continuation !== null} onPlay={prepareAudioPlayback}
                                        timeDrawerOpen={showTimeDrawer} onTimeDrawerChange={changeTimeDrawer}
                                        onSeek={cancelTimeAdjustment} />
                                ) : (
                                    <div className='trackPlaybackStartActions'>
                                        <button
                                            type='button'
                                            className='trackPlaybackStartButton episodePlaybackGlass'
                                            onClick={() => { prepareAudioPlayback(); startPlayback(); }}
                                            aria-label={initialPlayLabel.accessibilityLabel}
                                        >
                                            <PlayArrowRounded aria-hidden='true' />
                                            <span>{initialPlayLabel.title}</span>
                                        </button>
                                        {hasSharedStart && sharedStartSeconds > 0 && (
                                            <button
                                                type='button'
                                                className='trackSharedStartBeginning'
                                                onClick={() => playSharedMoment(0)}
                                            >
                                                Start from beginning
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div >
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
    forMeSessionRevision: state.user.forMeSessionRevision,
    track: state.data.track,
})

const mapActionsToProps = {
    setSearchText,
    setTrack,
}

export default connect(mapStateToProps, mapActionsToProps)(AudioPlayer);
